import React, {useEffect, useRef, useState} from 'react';
import TemplateDefault from "../../../components/template-default";
import Card from "../../../components/card";
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import orderService from "../../../services/order-service";
import InputDateTime from "../../../components/input-datetime";
import InputForm from "../../../components/input-form";
import SelectComponent from "../../../components/select-component";
import AttachesTable from "../../../components/attaches-table";
import SolutionsTable from "../components/solutions-table";
import PendTable from "../components/pend-table";
import Button from "../../../components/button";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const MySwal = withReactContent(Swal);

const OrderForm = () => {
    const initialState = {
        date_initial: new Date(),
        date_final: "",
        technicians: [],
        solutions: [],
        type: "Presencial",
        attachs: [],
        pend_multiway: false,
        pend_multiway_desc: "",
        pend_multiway_id_solution: "",
        pend_multiway_dt: "",
        pend_client: false,
        pend_client_desc: "",
        pend_client_id_solution: "",
        pend_client_dt: "",
        pend_terceiros: false,
        pend_terceiros_desc: "",
        pend_terceiros_id_solution: "",
        pend_terceiros_dt: "",
    }

    const [state, setState] = useState(initialState);

    const {id, hardware_attendance_id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const navigate = useNavigate();

    const attachRef = useRef();

    const {data} = useQuery({
        queryFn: () => orderService.formOptions(hardware_attendance_id, id).then(({data}) => data),
        queryKey: ['formOptions', {hardware_attendance_id, id}]
    });

    const {isError} = useQuery({
        queryFn: () => orderService.get(id).then(data => {
            setState((prevState) => {
                return Object.keys(prevState).reduce((acc, key) => {
                    if ((key.includes("date") || key.includes("_dt")) && data[key]) data[key] = new Date(data[key]);

                    acc[key] = data[key] ? data[key] : prevState[key];
                    return acc;
                }, {});
            });

            return data;
        }),
        queryKey: ['order', {id, hardware_attendance_id}],
        enabled: !!parseInt(id),
        retry: 0
    });

    if (isError) navigate("/404");

    const handleCustomInput = (id, value) => setState(prev => ({...prev, [id]: value}));

    const submit = () => {
        setIsLoading(true);

        const formData = new FormData();

        const form = {...state};

        Object.keys(form)
            .filter(key => !["technicians", "solutions", "type", "attach"].includes(key))
            .forEach(key => {
                if ((key.includes("date") || key.includes("_dt")) && form[key]) form[key] = form[key].toISOString();

                formData.append(key, form[key])
            });

        formData.append("type", form.type?.value ?? form.value ?? "");
        formData.append("technicians", JSON.stringify(form.technicians.map(t => t.value)));

        const attachesRow = [];
        attachRef.current?.api.forEachNode((node) => {
            attachesRow.push(node.data);

            if (node.data?.file) formData.append("attaches[]", node.data.file);
        });

        formData.append("attachs", JSON.stringify(attachesRow));

        let validate = true;

        const s = state.solutions.map(s => {
            if (!s.item || !s.action) {
                return validate = false;
            }
            return {item_id : s.item.value, action_id: s.action.value, id: s.id}
        } )

        formData.append("solutions", JSON.stringify(s));
        formData.append("hardware_attendance_id", hardware_attendance_id);

        if (!validate) {
            toast.error(AlertMessage.solutionsEmptyField);
            setIsLoading(false);
            return
        }

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? orderService.edit(formData, id) : orderService.create(formData);

        response
            .then(() => {
                toast.success("Salvo com sucesso.");
                navigate(`/hardware-attendances/form/${hardware_attendance_id}`);
            })
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
            .finally(() => setIsLoading(false));
    }
    const deleteOrder = (e) => {
        e.preventDefault();

        MySwal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: "Tem certeza que deseja excluir a order de serviço?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                orderService.delete(id)
                    .then(() => {
                        navigate(`/hardware-attendances/form/${hardware_attendance_id}`)
                    })
                    .catch((err) => {
                        const {data: {message}} = err.response;
                        toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    return (
        <TemplateDefault title={"Cadastro de ordem de serviço" + (parseInt(id) ? ` #${id}` : "")} isLoading={isLoading}>
            <Card title={"Chamado #" + hardware_attendance_id + (id ? " - Ordem " + id : "")} headerClick={() => navigate(`/hardware-attendances/form/${hardware_attendance_id}`)} titleClass="w-fit cursor-pointer">
                <div className="flex flex-col lg:grid grid-cols-1 gap-4 lg:grid-cols-12">
                    <div className="col-span-2">
                        <InputDateTime id="date_initial" label="Abertura"
                                       onChange={(date) => handleCustomInput("date_initial", date)}
                                       selected={state.date_initial}
                                       showTime/>
                    </div>

                    <div className="col-span-2">
                        <InputDateTime id="date_final" label="Encerramento"
                                       onChange={(date) => handleCustomInput("date_final", date)} selected={state.date_final} showTime/>
                    </div>

                    <InputForm id="product_place" value={data?.productPlace ?? ""} col="col-span-8" label="Produto (Localização)" readOnly/>

                    <div className="col-span-8 text-gray-900">
                        <SelectComponent
                            instanceId="technician"
                            label="Técnicos"
                            options={data?.technicians ?? []}
                            value={state.technicians}
                            onChange={(value) => handleCustomInput("technicians", value)}
                            isMulti
                        />
                    </div>

                    <div className="col-span-4 text-gray-900">
                        <SelectComponent
                            instanceId="type"
                            label="Técnicos"
                            options={[
                                {value: "Presencial", label: "Presencial"},
                                {value: "Remoto", label: "Remoto"},
                            ]}
                            value={state.type}
                            onChange={(value) => handleCustomInput("type", value)}
                        />
                    </div>
                </div>
            </Card>

            <SolutionsTable
                items={data?.items ?? []}
                actions={data?.actions ?? []}
                solutions={state.solutions}
                setState={setState}
                state={state}
            />

            <PendTable state={state} setState={setState} ordersId={data?.ordersId ?? []}/>

            <AttachesTable attaches={state.attachs} gridRef={attachRef} submit={submit}/>

            <Card wrapperClass="mt-5">
                <div className="flex justify-end gap-3">
                    {
                        id &&
                        <Button target="danger" onClick={deleteOrder}>
                            Excluir
                        </Button>
                    }
                    <Button onClick={() => navigate(`/hardware-attendances/form/${hardware_attendance_id}`)} target="warning">
                        Cancelar
                    </Button>
                    <Button onClick={submit} target="success">
                        Salvar
                    </Button>
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default OrderForm;
